import { computed, Injectable, signal } from '@angular/core';

/** Aggregates loading state across the dashboard's chart loaders. Each
    chart calls `setLoading('<key>', true|false)` from its load effect; the
    top progress bar reads `isLoading` and shows whenever ANY channel is
    active. Keys are arbitrary strings — convention is the chart selector
    (`'totalKpis'`, `'salesOrganisation'`, etc.).

    Why a service instead of a `viewChild` aggregation in the menu bar:
    the dashboard cards are siblings, not children, of the menu bar — they
    don't share a parent template that could `@if` on a combined flag. */
@Injectable()
export class SalesStatisticsLoadingService {
  private readonly channels = signal<ReadonlyMap<string, boolean>>(new Map());

  readonly isLoading = computed(() => {
    for (const v of this.channels().values()) {
      if (v) return true;
    }
    return false;
  });

  /** Wall-clock ms of the last full dashboard load (first channel active →
   *  all channels idle), shown next to the reload button. */
  readonly lastLoadDurationMs = signal<number | null>(null);
  private loadStartedAt: number | null = null;

  setLoading(key: string, loading: boolean): void {
    this.channels.update((current) => {
      const existing = current.get(key) ?? false;
      if (existing === loading) return current;
      const next = new Map(current);
      if (loading) next.set(key, true);
      else next.delete(key);
      return next;
    });
    const active = this.isLoading();
    if (active && this.loadStartedAt === null) {
      this.loadStartedAt = performance.now();
    } else if (!active && this.loadStartedAt !== null) {
      this.lastLoadDurationMs.set(performance.now() - this.loadStartedAt);
      this.loadStartedAt = null;
    }
  }
}
